import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import umService from "../../services/university-admin/umService";
import { useNotification } from "../../context/NotificationContext";
import { uploadPhoto as uploadUMPhoto } from "../../services/university-admin/api";
import optimizeImageFile from "../../utils/imageOptimizer";
import imageService from "../../services/imageService";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";

const SCOPE_ORDER = [
  "CLUB_ONLY",
  "CAMPUS_ONLY",
  "UNIVERSITY_ONLY",
  "COUNTRY_ONLY",
  "PUBLIC",
];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const toDateTimeLocal = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromDateTimeLocal = (value) =>
  value ? new Date(value).toISOString() : "";

const UMEventForm = ({ event, onSubmit, onCancel, loading }) => {
  const { t } = useTranslation("universityManager");
  const { t: tCommon } = useTranslation("common");
  const { t: tErrors } = useTranslation("errors");
  const { showError } = useNotification();
  const { can } = useUniAdminAuth();
  const canListCampuses = can(PERMISSIONS.campus.list);
  const canListClubs = can(PERMISSIONS.club.list);

  const isEdit = Boolean(event);

  const [organizerType, setOrganizerType] = useState("university");
  const [formData, setFormData] = useState({
    campusId: "",
    clubId: "",
    titleEn: "",
    titleLocal: "",
    descriptionEn: "",
    descriptionLocal: "",
    startDate: "",
    endDate: "",
    participationScope: "UNIVERSITY_ONLY",
    participationType: "PHYSICAL",
    price: "",
    quota: "",
    imageEnId: "",
    imageLocalId: "",
  });
  const [ticketAttendants, setTicketAttendants] = useState([]);
  const [newAttendantEmail, setNewAttendantEmail] = useState("");
  const [errors, setErrors] = useState({});

  const [campuses, setCampuses] = useState([]);
  const [clubs, setClubs] = useState([]);

  const [imageEnFile, setImageEnFile] = useState(null);
  const [imageEnPreview, setImageEnPreview] = useState(null);
  const [imageLocalFile, setImageLocalFile] = useState(null);
  const [imageLocalPreview, setImageLocalPreview] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => {
    if (canListCampuses) {
      umService
        .getAllCampuses({})
        .then((res) => setCampuses(res.success ? res.data || [] : []));
    }
    if (canListClubs) {
      umService
        .getAllClubs({})
        .then((res) => setClubs(res.success ? res.data || [] : []));
    }
  }, [canListCampuses, canListClubs]);

  useEffect(() => {
    if (!event) return;
    setOrganizerType(!event.isUniversityEvent ? "club" : "university");
    setFormData({
      campusId: "", // Organizer is not editable anyway
      clubId: "", // Organizer is not editable anyway
      titleEn: event.titleEn || "",
      titleLocal: event.titleLocal || "",
      descriptionEn: event.descriptionEn || "",
      descriptionLocal: event.descriptionLocal || "",
      startDate: toDateTimeLocal(event.startDate),
      endDate: toDateTimeLocal(event.endDate),
      participationScope: event.participationScope || "UNIVERSITY_ONLY",
      participationType: event.participationType || "PHYSICAL",
      price: event.price != null ? String(event.price) : "",
      quota: event.quota != null ? String(event.quota) : "",
      imageEnId: event.imageEnId ? String(event.imageEnId) : "",
      imageLocalId: event.imageLocalId ? String(event.imageLocalId) : "",
    });
    setImageEnPreview(
      event.imageUrlEn || imageService.getImageUrl(event.imageEnId),
    );
    setImageLocalPreview(
      event.imageUrlLocal ||
        event.imageUrlTr ||
        imageService.getImageUrl(event.imageLocalId),
    );
  }, [event]);

  const handleImageChange = (lang) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showError(
        t("crud.errors.invalidImageType", "Please select a valid image file."),
      );
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showError(
        t("crud.errors.imageTooLarge", "Image must be smaller than 5MB."),
      );
      return;
    }
    const reader = new FileReader();
    if (lang === "en") {
      setImageEnFile(file);
      reader.onload = (ev) => setImageEnPreview(ev.target.result);
    } else {
      setImageLocalFile(file);
      reader.onload = (ev) => setImageLocalPreview(ev.target.result);
    }
    reader.readAsDataURL(file);
  };

  const uploadImages = async () => {
    const result = {};
    if (imageEnFile) {
      try {
        const fileToSend = await optimizeImageFile(imageEnFile);
        const res = await uploadUMPhoto(fileToSend);
        if (res && res.success) result.imageEnId = res.data.imageId;
        else {
          showError(
            t(
              "events.allEvents.validation.imageUploadFailed",
              "Failed to upload English image",
            ),
          );
          return null;
        }
      } catch (err) {
        // uniAdminApi interceptor already notifies translated errors; show a fallback
        showError(
          t(
            "events.allEvents.validation.imageUploadFailed",
            "Failed to upload English image",
          ),
        );
        return null;
      }
    }
    if (imageLocalFile) {
      try {
        const fileToSend = await optimizeImageFile(imageLocalFile);
        const res = await uploadUMPhoto(fileToSend);
        if (res && res.success) result.imageLocalId = res.data.imageId;
        else {
          showError(
            t(
              "events.allEvents.validation.imageUploadFailed",
              "Failed to upload local image",
            ),
          );
          return null;
        }
      } catch (err) {
        showError(
          t(
            "events.allEvents.validation.imageUploadFailed",
            "Failed to upload local image",
          ),
        );
        return null;
      }
    }
    return result;
  };

  const handleAddAttendant = () => {
    const email = newAttendantEmail.trim();
    if (!email) return;
    if (!EMAIL_REGEX.test(email)) {
      showError(t("events.allEvents.validation.invalidTicketEmail"));
      return;
    }
    if (ticketAttendants.includes(email)) {
      setNewAttendantEmail("");
      return;
    }
    setTicketAttendants((prev) => [...prev, email]);
    setNewAttendantEmail("");
  };

  const handleRemoveAttendant = (email) => {
    setTicketAttendants((prev) => prev.filter((e) => e !== email));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.titleEn.trim() && !formData.titleLocal.trim()) {
      nextErrors.title = t("events.allEvents.validation.titleRequired");
    }

    if (!isEdit) {
      if (organizerType === "university" && !formData.campusId) {
        nextErrors.campusId = t(
          "events.allEvents.validation.organizerRequired",
        );
      }
      if (organizerType === "club" && !formData.clubId) {
        nextErrors.clubId = t("events.allEvents.validation.organizerRequired");
      }
    }

    if (!formData.startDate) {
      nextErrors.startDate = t("events.allEvents.validation.startDateRequired");
    } else if (new Date(formData.startDate) <= new Date()) {
      nextErrors.startDate = t(
        "events.allEvents.validation.startDateMustBeFuture",
      );
    }

    if (!formData.endDate) {
      nextErrors.endDate = t("events.allEvents.validation.endDateRequired");
    } else if (new Date(formData.endDate) <= new Date()) {
      nextErrors.endDate = t("events.allEvents.validation.endDateMustBeFuture");
    }

    if (!formData.participationScope) {
      nextErrors.participationScope = t(
        "events.allEvents.validation.participationScopeRequired",
      );
    }
    if (!formData.participationType) {
      nextErrors.participationType = t(
        "events.allEvents.validation.participationTypeRequired",
      );
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setUploadingImages(true);
    const uploaded = await uploadImages();
    setUploadingImages(false);
    if (uploaded === null) return;

    const imageEnId = uploaded.imageEnId || formData.imageEnId;
    const imageLocalId = uploaded.imageLocalId || formData.imageLocalId;

    const payload = {
      titleEn: formData.titleEn.trim(),
      titleLocal: formData.titleLocal.trim(),
      descriptionEn: formData.descriptionEn.trim(),
      descriptionLocal: formData.descriptionLocal.trim(),
      startDate: fromDateTimeLocal(formData.startDate),
      endDate: fromDateTimeLocal(formData.endDate),
      participationScope: formData.participationScope,
      participationType: formData.participationType,
      ...(imageEnId && { imageEnId }),
      ...(imageLocalId && { imageLocalId }),
    };

    if (formData.price !== "") payload.price = parseFloat(formData.price) || 0;
    if (formData.quota !== "") payload.quota = parseInt(formData.quota, 10);

    if (!isEdit) {
      if (organizerType === "club") payload.clubId = formData.clubId;
      else payload.campusId = formData.campusId;
      if (ticketAttendants.length > 0)
        payload.ticketAttendants = ticketAttendants;
    }

    onSubmit(payload);
  };

  const isUniversityEditing = isEdit && event.isUniversityEvent;
  const scopeMinIndex = isEdit
    ? SCOPE_ORDER.indexOf(event.participationScope)
    : -1;

  const isBusy = loading || uploadingImages;

  return (
    <form onSubmit={handleSubmit} className="event-form">
      <div className="row g-4">
        {/* Organizer */}
        <div className="col-12">
          <div className="form-section">
            <div className="form-section-header">
              <i className="fas fa-university"></i>
              <h6>{t("events.allEvents.form.organizerType")}</h6>
            </div>
            {isEdit ? (
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-bold">
                    {isUniversityEditing
                      ? t("events.allEvents.form.universityEvent")
                      : t("events.allEvents.form.clubEvent")}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={
                      isUniversityEditing
                        ? event.campusName || event.campusNameEn || ""
                        : event.clubName || event.clubNameEn || ""
                    }
                    disabled
                  />
                  <small className="text-muted">
                    {t("events.allEvents.form.organizerNotEditable")}
                  </small>
                </div>
              </div>
            ) : (
              <>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <div className="btn-group w-100" role="group">
                      <button
                        type="button"
                        className={`btn ${organizerType === "university" ? "btn-primary" : "btn-outline-secondary"}`}
                        onClick={() => setOrganizerType("university")}
                        disabled={isBusy}
                      >
                        <i className="fas fa-university me-1"></i>
                        {t("events.allEvents.form.universityEvent")}
                      </button>
                      <button
                        type="button"
                        className={`btn ${organizerType === "club" ? "btn-primary" : "btn-outline-secondary"}`}
                        onClick={() => setOrganizerType("club")}
                        disabled={isBusy}
                      >
                        <i className="fas fa-users me-1"></i>
                        {t("events.allEvents.form.clubEvent")}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="row g-3">
                  {organizerType === "university" ? (
                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        {t("events.allEvents.form.selectCampus")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <select
                        className={`form-select ${errors.campusId ? "is-invalid" : ""}`}
                        value={formData.campusId}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            campusId: e.target.value,
                          }))
                        }
                        disabled={isBusy || !canListCampuses}
                      >
                        <option value="">
                          {t("events.allEvents.form.selectCampus")}
                        </option>
                        {campuses.map((campus) => (
                          <option key={campus.id} value={campus.id}>
                            {campus.nameEn}
                          </option>
                        ))}
                      </select>
                      {errors.campusId && (
                        <div className="invalid-feedback">
                          {errors.campusId}
                        </div>
                      )}
                      {!canListCampuses && (
                        <small className="text-danger">{tErrors("permission.denied")}</small>
                      )}
                    </div>
                  ) : (
                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        {t("events.allEvents.form.selectClub")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <select
                        className={`form-select ${errors.clubId ? "is-invalid" : ""}`}
                        value={formData.clubId}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            clubId: e.target.value,
                          }))
                        }
                        disabled={isBusy || !canListClubs}
                      >
                        <option value="">
                          {t("events.allEvents.form.selectClub")}
                        </option>
                        {clubs.map((club) => (
                          <option key={club.id} value={club.id}>
                            {club.name || club.nameLocal || club.nameEn}
                          </option>
                        ))}
                      </select>
                      {errors.clubId && (
                        <div className="invalid-feedback">{errors.clubId}</div>
                      )}
                      {!canListClubs && (
                        <small className="text-danger">{tErrors("permission.denied")}</small>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Basic info */}
        <div className="col-12">
          <div className="form-section">
            <div className="form-section-header">
              <i className="fas fa-info-circle"></i>
              <h6>{t("events.allEvents.form.basicInformation")}</h6>
            </div>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("events.allEvents.form.titleEn")}
                </label>
                <input
                  type="text"
                  maxLength={255}
                  className={`form-control ${errors.title ? "is-invalid" : ""}`}
                  value={formData.titleEn}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      titleEn: e.target.value,
                    }))
                  }
                  disabled={isBusy}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("events.allEvents.form.titleLocal")}
                </label>
                <input
                  type="text"
                  maxLength={255}
                  className={`form-control ${errors.title ? "is-invalid" : ""}`}
                  value={formData.titleLocal}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      titleLocal: e.target.value,
                    }))
                  }
                  disabled={isBusy}
                />
                {errors.title && (
                  <div className="invalid-feedback d-block">{errors.title}</div>
                )}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("events.allEvents.form.descriptionEn")}
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={formData.descriptionEn}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      descriptionEn: e.target.value,
                    }))
                  }
                  disabled={isBusy}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("events.allEvents.form.descriptionLocal")}
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={formData.descriptionLocal}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      descriptionLocal: e.target.value,
                    }))
                  }
                  disabled={isBusy}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Dates & participation */}
        <div className="col-12">
          <div className="form-section">
            <div className="form-section-header">
              <i className="fas fa-calendar-alt"></i>
              <h6>{t("events.allEvents.form.dateAndParticipation")}</h6>
            </div>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("events.allEvents.form.startDate")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="datetime-local"
                  className={`form-control ${errors.startDate ? "is-invalid" : ""}`}
                  value={formData.startDate}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                  disabled={isBusy}
                />
                {errors.startDate && (
                  <div className="invalid-feedback">{errors.startDate}</div>
                )}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("events.allEvents.form.endDate")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="datetime-local"
                  className={`form-control ${errors.endDate ? "is-invalid" : ""}`}
                  value={formData.endDate}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }))
                  }
                  disabled={isBusy}
                />
                {errors.endDate && (
                  <div className="invalid-feedback">{errors.endDate}</div>
                )}
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold">
                  {t("events.allEvents.form.participationScope")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${errors.participationScope ? "is-invalid" : ""}`}
                  value={formData.participationScope}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      participationScope: e.target.value,
                    }))
                  }
                  disabled={isBusy}
                >
                  {SCOPE_ORDER.map((scope) => {
                    const disabled =
                      (!isEdit &&
                        organizerType === "university" &&
                        scope === "CLUB_ONLY") ||
                      (isEdit &&
                        scopeMinIndex >= 0 &&
                        SCOPE_ORDER.indexOf(scope) < scopeMinIndex);
                    return (
                      <option key={scope} value={scope} disabled={disabled}>
                        {t(`events.allEvents.form.scopes.${scope}`, scope)}
                      </option>
                    );
                  })}
                </select>
                {errors.participationScope && (
                  <div className="invalid-feedback">
                    {errors.participationScope}
                  </div>
                )}
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold">
                  {t("events.allEvents.form.participationType")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${errors.participationType ? "is-invalid" : ""}`}
                  value={formData.participationType}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      participationType: e.target.value,
                    }))
                  }
                  disabled={isBusy}
                >
                  <option value="ONLINE">
                    {t("events.allEvents.form.types.ONLINE")}
                  </option>
                  <option value="PHYSICAL">
                    {t("events.allEvents.form.types.PHYSICAL")}
                  </option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label fw-bold">
                  {t("events.allEvents.form.price")}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, price: e.target.value }))
                  }
                  disabled={isBusy}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label fw-bold">
                  {t("events.allEvents.form.quota")}
                </label>
                <input
                  type="number"
                  min="1"
                  max="999999"
                  className="form-control"
                  value={formData.quota}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, quota: e.target.value }))
                  }
                  disabled={isBusy}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="col-12">
          <div className="form-section">
            <div className="form-section-header">
              <i className="fas fa-image"></i>
              <h6>{t("events.allEvents.form.images")}</h6>
            </div>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("events.allEvents.form.eventImageEn")}
                </label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleImageChange("en")}
                  disabled={isBusy}
                />
                {imageEnPreview && (
                  <div
                    className="mt-2 border rounded overflow-hidden"
                    style={{ maxWidth: "200px" }}
                  >
                    <img
                      src={imageEnPreview}
                      alt="EN preview"
                      className="img-fluid w-100"
                    />
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("events.allEvents.form.eventImageLocal")}
                </label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleImageChange("local")}
                  disabled={isBusy}
                />
                {imageLocalPreview && (
                  <div
                    className="mt-2 border rounded overflow-hidden"
                    style={{ maxWidth: "200px" }}
                  >
                    <img
                      src={imageLocalPreview}
                      alt="Local preview"
                      className="img-fluid w-100"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Ticket attendants — create only */}
        {!isEdit && (
          <div className="col-12">
            <div className="form-section">
              <div className="form-section-header">
                <i className="fas fa-id-badge"></i>
                <h6>{t("events.allEvents.form.ticketAttendants")}</h6>
              </div>
              <div className="row g-2 align-items-end mb-2">
                <div className="col-md-8">
                  <input
                    type="email"
                    className="form-control"
                    placeholder={t("events.allEvents.form.emailPlaceholder")}
                    value={newAttendantEmail}
                    onChange={(e) => setNewAttendantEmail(e.target.value)}
                    disabled={isBusy}
                  />
                </div>
                <div className="col-md-4">
                  <button
                    type="button"
                    className="btn btn-outline-primary w-100"
                    onClick={handleAddAttendant}
                    disabled={isBusy}
                  >
                    <i className="fas fa-plus me-1"></i>
                    {t("events.allEvents.form.addTicketAttendant")}
                  </button>
                </div>
              </div>
              {ticketAttendants.length > 0 && (
                <ul className="list-group">
                  {ticketAttendants.map((email) => (
                    <li
                      key={email}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      {email}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleRemoveAttendant(email)}
                        disabled={isBusy}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onCancel}
          disabled={isBusy}
        >
          {tCommon("buttons.cancel", "Cancel")}
        </button>
        <button type="submit" className="btn btn-primary" disabled={isBusy}>
          {uploadingImages
            ? t("events.allEvents.form.uploadingImage")
            : loading
              ? tCommon("actions.saving", "Saving...")
              : isEdit
                ? t("events.allEvents.form.updateEvent")
                : t("events.allEvents.form.createEvent")}
        </button>
      </div>
    </form>
  );
};

export default UMEventForm;
